import { Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { INavItem } from '../../interfaces';
import { APP_ROUTES } from '../../enums';
import { getExplorisHealthUrl } from '../../utils/url.utils';
import { UserProfileService } from 'src/app/core/services/user-profile.service';
import { LayoutUiService } from '../../services';

@Component({
  standalone: false,
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent {
  isCollapsed = false;
  getExplorisHealthUrl = getExplorisHealthUrl;
  readonly sidebarOpen$: Observable<boolean> = this.layoutUiService.sidebarOpen$;

  navItems: INavItem[] = [
    {
      label: 'shared.components.sidebar.nav.home',
      route: APP_ROUTES.HOME,
      icon: 'assets/icons/home.svg',
      clinicalOnly: true,
    },
    {
      label: 'shared.components.sidebar.nav.test_results',
      route: APP_ROUTES.TESTS,
      icon: 'assets/icons/activity.svg',
    },
  ];

  adminNavItems: INavItem[] = [
    {
      label: 'shared.components.sidebar.nav.customers',
      route: '/admin/customers',
      icon: 'assets/icons/users-profile.svg',
    },
    {
      label: 'shared.components.sidebar.nav.customer_requests',
      route: '/admin/customer-requests',
      icon: 'assets/images/email.svg',
    },
    {
      label: 'shared.components.sidebar.nav.export',
      route: '/admin/export',
      icon: 'assets/icons/export.svg',
    },
    {
      // HK-156 — server-to-server API client onboarding + management.
      label: 'shared.components.sidebar.nav.api_clients',
      route: '/admin/api-clients',
      icon: 'assets/icons/file.svg',
    },
  ];

  isAdminMode$: Observable<boolean> = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.router.url === '/admin' || this.router.url.startsWith('/admin/'))
  );

  isSuperUser$: Observable<boolean> = this.userProfileService
    .getProfileObservable()
    .pipe(map(profile => profile?.isSuperUser === true));

  secondaryNavItems: INavItem[] = [
    {
      label: 'shared.components.sidebar.nav.settings',
      route: APP_ROUTES.SETTINGS,
      icon: 'assets/icons/settings.svg',
      isExternal: false,
    },
    {
      label: 'shared.components.sidebar.nav.contact_us',
      route: '/?contact',
      icon: 'assets/images/email.svg',
      isExternal: true,
      clinicalOnly: true,
    },
    {
      label: 'shared.components.sidebar.nav.help',
      route: '/product',
      icon: 'assets/icons/help.svg',
      isExternal: true,
      clinicalOnly: true,
    },
  ];

  constructor(
    private router: Router,
    private userProfileService: UserProfileService,
    private layoutUiService: LayoutUiService
  ) {}

  /**
   * @description Dismisses the drawer from its own close button.
   * @returns {void} void
   */
  closeSidebar(): void {
    this.layoutUiService.closeSidebar();
  }

  /**
   * @description Toggles sidebar collapse state.
   */
  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  /**
   * @description Returns the `/tests` route in clinical mode and the
   * `/admin/tests` route in admin mode. Resolved synchronously per
   * change-detection cycle so the template can bind without an async pipe.
   * @returns {string} routerLink target for the Tests / Test results item
   */
  testResultsRoute(isAdmin: boolean | null): string {
    return isAdmin ? '/' + APP_ROUTES.ADMIN_TESTS : '/' + APP_ROUTES.TESTS;
  }

  /**
   * @description Mode-aware label for the tests nav item: "Tests" in the admin
   * perspective, "Test results" in the clinical perspective.
   * @returns {string} i18n key for the label
   */
  testResultsLabel(isAdmin: boolean | null): string {
    return isAdmin ? 'shared.components.sidebar.nav.tests' : 'shared.components.sidebar.nav.test_results';
  }

  /**
   * @description Returns the `/settings` route in clinical mode and the
   * `/admin/settings` alias in admin mode. Both resolve to the same
   * `SettingsComponent`; routing under `/admin` keeps the super user in the
   * Admin perspective instead of flipping back to clinical.
   * @returns {string} routerLink target for the Settings item
   */
  settingsRoute(isAdmin: boolean | null): string {
    return isAdmin ? '/' + APP_ROUTES.ADMIN_SETTINGS : '/' + APP_ROUTES.SETTINGS;
  }
}
